import React, { useCallback } from 'react';
import { Button, TextField, Toolbar } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { IFacetSection } from 'api/catalog/models';
import { useFacets } from 'hooks';
import DelimitedItemInput from 'ui/DelimitedItemInput';

import FacetSectionWrapper from '../FacetSectionWrapper';
import FacetSection from '../FacetSection';
import messages from '../messages';

const IS_ACCORDION_OPEN_DEFAULT = false;

interface IProps {
  facets: IFacetSection[];
  onCancel?: () => void;
  onApplied?: () => void;
}

const Facets = ({
  facets,
  onCancel = () => null,
  onApplied = () => null,
}: IProps) => {
  const { formatMessage } = useIntl();
  const {
    isFacetSelected,
    getSelectedCount,
    handleSelectionChange,
    handleSelectAllChange,
    handleReset,
    applyFacets,
    searchTerm,
    itemSearchType,
    itemSearchValues,
    handleSearchChange,
    handleItemSearchChange,
  } = useFacets();

  const apply = useCallback(() => {
    applyFacets();
    onApplied();
  }, [applyFacets, onApplied]);

  return (
    <>
      <FacetSectionWrapper
        title={formatMessage(messages.searchTermSection)}
        isOpen={IS_ACCORDION_OPEN_DEFAULT}
        selectedCount={searchTerm.length > 0 ? 1 : 0}
      >
        <TextField
          fullWidth
          id='outlined-basic'
          label={formatMessage(messages.searchLabel)}
          variant='outlined'
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </FacetSectionWrapper>
      <FacetSectionWrapper
        title={formatMessage(messages.itemSearchSection)}
        isOpen={IS_ACCORDION_OPEN_DEFAULT}
        selectedCount={itemSearchValues.length > 0 ? 1 : 0}
      >
        <DelimitedItemInput
          type={itemSearchType}
          onChange={handleItemSearchChange}
          values={itemSearchValues}
        />
      </FacetSectionWrapper>
      {facets.map((facetSection) => (
        <FacetSectionWrapper
          key={facetSection.field}
          title={facetSection.name}
          isOpen={IS_ACCORDION_OPEN_DEFAULT}
          selectedCount={getSelectedCount(facetSection.field)}
        >
          <FacetSection
            facetSection={facetSection}
            selectedCount={getSelectedCount(facetSection.field)}
            onChangeSelectAll={(allFacets) =>
              handleSelectAllChange(facetSection.field, allFacets)
            }
            onChangeSelection={(changedFacets) =>
              handleSelectionChange(facetSection.field, changedFacets)
            }
            isSelected={(facet) => isFacetSelected(facetSection.field, facet)}
          />
        </FacetSectionWrapper>
      ))}
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Button variant='outlined' onClick={handleReset}>
          <FormattedMessage {...messages.resetButton} />
        </Button>
        <Button variant='outlined' onClick={onCancel}>
          <FormattedMessage {...messages.cancelButton} />
        </Button>
        <Button variant='contained' onClick={apply}>
          <FormattedMessage {...messages.applyButton} />
        </Button>
      </Toolbar>
    </>
  );
};

export default Facets;
